import { PreviewPlan, PreviewPlanLayer, PreviewPlanSlide } from 'api/video';

/**
 * SYM-6206. Where everything is at a given moment of a Track 1 preview.
 *
 * ⚠️ THIS DECIDES NOTHING ABOUT HOW THE VIDEO LOOKS. The pictures, their sizes,
 * their line breaks, their colours and their positions at rest were all settled
 * by the server, which drew them with the same code that composites the video.
 * All that happens here is arithmetic over time: which picture is on screen at
 * second t, and how far it still has to travel.
 *
 * Every rule below is ffmpeg's, restated (sympl-api `OverlayFilterBuilder`):
 * change one and the preview stops telling the truth about the render.
 */

/** A layer's placement at one moment. */
export interface LayerPlacement {
  visible: boolean;
  /** Pixels on the video's own canvas, not on the screen. */
  x: number;
  y: number;
}

/**
 * How far a layer still has to travel: 1 when it starts, 0 once it has landed.
 *
 * quadratic-out is (1 - p)², which covers most of the distance immediately and
 * eases into place. An easing we do not know degrades to linear rather than
 * throwing -- slightly different movement is a far better failure than a blank
 * preview.
 */
export const travelRemaining = (
  elapsed: number,
  duration: number,
  easing: string
): number => {
  if (duration <= 0) return 0;

  const p = Math.min(Math.max(elapsed / duration, 0), 1);

  return easing === 'quadratic-out' ? (1 - p) ** 2 : 1 - p;
};

/**
 * Where one layer sits at second `t`, and whether it is on screen at all.
 *
 * `ends_at` null means it stays to the last frame -- the brand wash, the logo.
 */
export const placeLayer = (
  layer: PreviewPlanLayer,
  t: number
): LayerPlacement => {
  const visible =
    t >= layer.starts_at && (layer.ends_at === null || t <= layer.ends_at);

  if (!layer.animation) {
    return { visible, x: layer.x, y: layer.y };
  }

  const remaining = travelRemaining(
    t - layer.starts_at,
    layer.animation.duration,
    layer.animation.easing
  );

  return {
    visible,
    x: layer.x + layer.animation.distance * remaining,
    y: layer.y,
  };
};

/**
 * Which photo is showing at second `t`, and the one it is crossing into.
 *
 * ffmpeg crosses from one photo to the next with an xfade that starts at the
 * transition's own moment -- which for a cut is one frame BEFORE the slide
 * begins, because xfade cannot do a zero-length transition. Reproducing that
 * here rather than "swap at the slide's start" is what keeps a cut landing on
 * the same frame in the browser as in the video.
 *
 * `mix` is 0 while only `from` shows and 1 once `to` has fully arrived.
 */
export const photoAt = (
  slides: PreviewPlanSlide[],
  t: number
): { from: number; to: number | null; mix: number } => {
  let from = 0;

  for (let i = 1; i < slides.length; i++) {
    const { starts_at: start, duration } = slides[i].transition;

    if (t < start) break;

    if (t < start + duration && duration > 0) {
      return { from: i - 1, to: i, mix: (t - start) / duration };
    }

    from = i;
  }

  return { from, to: null, mix: 0 };
};

/**
 * SYM-6298, 21 September 2026. The pictures the browser should be fetching now.
 *
 * A photo used to be asked for only at the moment it was needed, which a
 * cross-fade could hide -- the outgoing photo stayed where it was and simply
 * showed through. A slide cannot hide it: the outgoing photo moves away and
 * whatever has not arrived is bare stage. So every slide's picture is put in
 * the page and fetched long before its own changeover is due.
 *
 * ⚠️ ALL OF THEM AT ONCE, AND NOTHING WAITS ON ANYTHING ELSE. 22 September
 * 2026: this used to ask for the leading picture alone and let the rest follow
 * once it had finished, so that four requests could not share one slow
 * connection four ways. Review showed what that costs -- a picture that never
 * answers stalls the chain behind it, and every later picture in the video is
 * then never asked for at all. One address that hangs must not be able to
 * withhold the rest, so the queueing is left to the browser, which is the only
 * thing that actually knows about the connection. It is told which one matters
 * most instead: the leading picture is the frame on screen at second nought and
 * is marked urgent, the rest can wait their turn.
 *
 * The same picture used on two slides is fetched once.
 */
export const picturesToPrefetch = (
  slides: PreviewPlanSlide[]
): Array<{ url: string; priority: 'high' | 'low' }> => {
  const urls = Array.from(
    new Set(slides.map((slide) => slide.url).filter((url) => !!url))
  );

  return urls.map((url, place) => ({
    url,
    priority: place === 0 ? 'high' : 'low',
  }));
};

/**
 * SYM-6298, 21 September 2026. Which changeover is running and how far it has
 * got -- which is nowhere at all until the picture it is bringing in is here.
 *
 * A changeover that moves the outgoing photo aside before the incoming one has
 * arrived uncovers the stage, and black under the wording is worse than a
 * change that starts late. So the movement waits: the outgoing photo stays put,
 * covering the whole frame, and the incoming one waits one frame away. The
 * moment the picture arrives the changeover carries on from wherever the clock
 * has reached, so the slide still LANDS when the rendered file lands and only
 * the travelling is cut short.
 *
 * Nothing here holds anything up once the pictures are in hand, which after
 * prefetching is every ordinary case: `mix` is then the clock's own, and the
 * movement is exactly what it would have been.
 *
 * ⚠️ AND THE HOLD ONLY EARNS ITS KEEP WHILE THERE IS SOMETHING TO PROTECT.
 * 22 September 2026, raised in review: the whole reason to keep the outgoing
 * photo still is that it is covering the frame. If that photo has not arrived
 * either, the frame is bare whatever we do, and holding then only keeps a
 * picture that IS here off the screen -- strictly worse than letting the
 * changeover run. So the hold asks after both: it waits only when the photo
 * being left behind is really there to cover for the one that is late.
 *
 * A picture that failed never enters `arrived`, so a changeover away from a
 * photo that is here never plays. That is deliberate: the slide has nothing to
 * move to, and holding keeps the frame covered instead of wiping to black.
 */
export const changeoverShown = (
  slides: PreviewPlanSlide[],
  photo: { from: number; to: number | null; mix: number },
  arrived: ReadonlySet<string>
): { name: string; mix: number } => {
  if (photo.to === null) return { name: 'fade', mix: 0 };

  const here = (slide: PreviewPlanSlide | undefined) =>
    !!slide?.url && arrived.has(slide.url);

  const arriving = slides[photo.to];
  const name = arriving?.transition.name ?? 'fade';

  if (here(arriving)) return { name, mix: photo.mix };

  return { name, mix: here(slides[photo.from]) ? 0 : photo.mix };
};

/** Where one of the two photos sits during a changeover, and how solid it is. */
export interface PhotoSpot {
  /** Sideways shift as a percentage of the frame's width. */
  x: number;
  /** Up-and-down shift as a percentage of the frame's height. */
  y: number;
  opacity: number;
}

/**
 * SYM-6298. WHICH WAY the changeover moves, not just how far along it is.
 *
 * The server names every changeover with ffmpeg's own name for it
 * (`Track1Transition::XFADE` in sympl-api sends fade, slideleft, slideright,
 * slideup, slidedown). Until now the preview read only the progress and drew
 * every one of them as a cross-fade, so a look whose slides are meant to push
 * each other off the screen looked nothing like the file that came back.
 * Failed by hand as test T1-58 on 21 September 2026.
 *
 * A slide moves BOTH photos together, by the same distance, so they stay edge
 * to edge and always cover the frame exactly once: at progress m the outgoing
 * photo has travelled m of a frame in the named direction and the incoming one
 * is the remaining (1 - m) behind it. `slideleft` is ffmpeg's: the picture
 * travels leftwards, so the new photo comes in from the RIGHT edge.
 *
 * Both photos stay fully solid while they move -- the movement is what shows
 * the change, and fading as well would be a second effect ffmpeg never applies.
 *
 * A cut needs no case of its own: the server sends it as a `fade` one frame
 * long. A name we do not know falls back to the fade, because a changeover
 * drawn slightly wrong is a far better failure than a blank preview.
 */
export const photoPlacement = (
  name: string,
  mix: number
): { from: PhotoSpot; to: PhotoSpot } => {
  const m = Math.min(Math.max(mix, 0), 1);
  // A distance of "minus nothing" is nothing. Multiplying by a progress of 0
  // yields JavaScript's negative zero, which compares equal to zero but is not
  // the same value to anything that looks at it closely, and reads as "-0%" in
  // the CSS. `n === 0 ? 0` is true for both zeroes and hands back the plain one.
  const flat = (n: number) => (n === 0 ? 0 : n);
  const solid = (x: number, y: number): PhotoSpot => ({
    x: flat(x),
    y: flat(y),
    opacity: 1,
  });

  switch (name) {
    case 'slideleft':
      return { from: solid(-100 * m, 0), to: solid(100 * (1 - m), 0) };
    case 'slideright':
      return { from: solid(100 * m, 0), to: solid(-100 * (1 - m), 0) };
    case 'slideup':
      return { from: solid(0, -100 * m), to: solid(0, 100 * (1 - m)) };
    case 'slidedown':
      return { from: solid(0, 100 * m), to: solid(0, -100 * (1 - m)) };
    default:
      return { from: solid(0, 0), to: { x: 0, y: 0, opacity: m } };
  }
};

/**
 * SYM-6221. How big the photo is drawn at second `t`: the Dynamic drift.
 *
 * A straight line from `scale_from` at the slide's own start to `scale_to` at
 * its end, and held there after. No easing: ffmpeg's expression is linear in t.
 * A slide with no drift (every look but Dynamic) is drawn at 1, still.
 *
 * The clock is the SLIDE'S, not the transition's. While one photo crosses into
 * the next both are on screen, and each keeps growing on its own clock, so the
 * outgoing photo never snaps back: its window runs to the end of the overlap,
 * exactly as ffmpeg's input for it does.
 */
export const photoScale = (slide: PreviewPlanSlide, t: number): number => {
  const drift = slide.drift;

  if (!drift) return 1;
  if (slide.duration <= 0) return drift.scale_to;

  const p = Math.min(Math.max((t - slide.start) / slide.duration, 0), 1);

  return drift.scale_from + (drift.scale_to - drift.scale_from) * p;
};

/**
 * The CSS that grows the photo about the point the server aimed at.
 *
 * `transform-origin` is the drift's origin as percentages of the frame, so a
 * scale about it keeps that point where it is and lets the rest of the photo
 * move outward -- what ffmpeg does when it scales and then crops so that the
 * same fraction of the overflow falls on each side of the origin.
 */
export const driftStyle = (
  slide: PreviewPlanSlide,
  t: number
): { transform: string; transformOrigin: string } => {
  const origin = slide.drift?.origin ?? { x: 0.5, y: 0.5 };

  return {
    transform: `scale(${photoScale(slide, t)})`,
    transformOrigin: `${origin.x * 100}% ${origin.y * 100}%`,
  };
};

/** The video's own shape, for a pane that must not letterbox or crop it. */
export const planAspect = (plan: PreviewPlan): number =>
  plan.height > 0 ? plan.width / plan.height : 9 / 16;

/**
 * The crop as CSS percentages, from the fractions the server sends.
 *
 * The photo is drawn to cover the frame after cropping, the same way ffmpeg
 * crops and then scales with `force_original_aspect_ratio=increase`.
 *
 * SYM-6272. `maxWidth`/`maxHeight: none` are part of the crop, not decoration.
 * The global reset caps every <img> at `max-width: 100%`. A cropped photo has
 * to be drawn LARGER than the frame (keep the left 37 % and it is 267 % wide),
 * so under that cap it was squeezed back to the frame's width and the preview
 * showed the uncropped photo while the video was cropped. Measured in the
 * browser on 19 Sep 2026: style width 266.8 %, box on screen 384 px, the width
 * of its parent.
 */
export const cropStyle = (
  slide: PreviewPlanSlide
): {
  width: string;
  height: string;
  left: string;
  top: string;
  maxWidth: 'none';
  maxHeight: 'none';
} => {
  const crop = slide.crop ?? { x: 0, y: 0, width: 1, height: 1 };
  const width = crop.width > 0 ? crop.width : 1;
  const height = crop.height > 0 ? crop.height : 1;

  return {
    width: `${(1 / width) * 100}%`,
    height: `${(1 / height) * 100}%`,
    left: `${(-crop.x / width) * 100}%`,
    top: `${(-crop.y / height) * 100}%`,
    maxWidth: 'none',
    maxHeight: 'none',
  };
};
